// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/* Sprint 2.4.0 -- Incident Operations Level 2 */
// ── Playbook Templates (grc_incident_playbook_templates) ──────────────
// Sprint 2.4.0 base + Sprint 31.6.0 PDPL/Telecom scenarios. scenario_type is
// stored as a free-form string server-side (no enum gate), so this list is the
// documented canonical set rather than a hard runtime constraint.
//
// fix-sprint-20260627-2217-followup Finding 1 — the list is a runtime value so
// the seed (`PlaybookTemplateService.seedDefaults`), its test and the Playbook
// Templates page read ONE vocabulary. Four of the six seeded templates had been
// written with values outside it (`unauthorized_access`, `vendor_incident`,
// `human_error`, `physical_security`), so no scenario filter found them and the
// edit form opened with an empty required select.
export const PLAYBOOK_SCENARIO_TYPES = [
    'data_breach',
    'ransomware',
    'insider_threat',
    'third_party_breach',
    'system_compromise',
    'physical_breach',
    'social_engineering',
    'denial_of_service',
    'generic',
    'pdpl_breach',
    'telecom_disclosure',
];
export const PLAYBOOK_TASK_CATEGORIES = [
    'containment',
    'investigation',
    'remediation',
    'notification',
    'communication',
    'documentation',
];
export const PLAYBOOK_TASK_PRIORITIES = ['low', 'medium', 'high', 'critical'];
// ── Regulator Exports (grc_incident_regulator_exports) ────────────────
/**
 * fix-sprint-20261004-63-8-2 F4 — the formats `RegulatorExportService.create` accepts and the exports
 * page offers. The type used to say `'pdf' | 'zip'`, which neither did.
 */
export const REGULATOR_EXPORT_FORMATS = ['pdf', 'json', 'xml', 'csv'];
/** fix-sprint-20261004-63-8-2 F4 — the watermarks the exports page offers (`none` included). */
export const REGULATOR_WATERMARKS = ['draft', 'confidential', 'final', 'none'];
// ── Vendor Coordination (grc_incident_vendor_coordination) ────────────
/**
 * Sprint 63.30.0 (#587, locked decision 3) — the ONE vendor-coordination status
 * vocabulary, read by `VendorCoordinationService.updateStatus` (which refuses
 * anything else) and by `VendorCoordinationPage` (which offers exactly these and
 * exactly the transitions below). The page used to carry its own copy with three
 * statuses the service refused, and this type named six values neither side
 * used; `vendor-coordination-vocabulary-63.30.0.test.ts` holds the two ends to
 * this list.
 */
export const VENDOR_COORDINATION_STATUSES = [
    'initiated',
    'in_progress',
    'awaiting_vendor',
    'vendor_responded',
    'resolved',
    'closed',
    'escalated',
];
/** The legal next statuses from each status (Sprint 63.30.0, #587). */
export const VENDOR_COORDINATION_TRANSITIONS = {
    initiated: ['in_progress', 'awaiting_vendor', 'escalated', 'closed'],
    in_progress: ['awaiting_vendor', 'vendor_responded', 'resolved', 'escalated', 'closed'],
    awaiting_vendor: ['vendor_responded', 'escalated', 'closed'],
    vendor_responded: ['in_progress', 'resolved', 'escalated', 'closed'],
    resolved: ['closed'],
    closed: [],
    escalated: ['in_progress', 'awaiting_vendor', 'closed'],
};
/**
 * The kinds of regulatory clock incident-ops can seed and render.
 *
 * Exported as a value array with the union derived from it, the way
 * `REGULATORY_FRAMEWORK_STATUSES` is, so a seeder or a label table can validate
 * against the vocabulary rather than restate it. This file's own history is the
 * argument: two hand-copied status allowlists had already drifted from one union
 * in opposite directions at once.
 *
 * ## `subject_notification` — Sprint 65.9.5
 *
 * The first four members all name a **dealing with the authority**. None of them
 * could express a notification owed to the data subject, so
 * `seedRegulatorySlaDefaults`'s `WANTED` table could only list the authority
 * limb — and the consequence was not a missing label. The subject-notification
 * clock was **invisible in incident-ops entirely**, because there was no
 * `deadline_type` to seed it under.
 *
 * That is content the platform already held. Five corpus records declare such a
 * duty: `incident_subject_notification`, `telecom_breach_subject_notification`,
 * `breach_communication_to_data_subjects`, and two authoring
 * `incident_authority_and_subject_notification`.
 *
 * And it is the duty one least wants invisible. An authority notification that
 * slips is a sanction; a subject notification that slips is a person who was not
 * told their data was exposed, and who therefore did not change a password,
 * freeze a card, or watch for fraud.
 *
 * It is a SEPARATE member rather than a flag on `initial_notification` because
 * the two are two clocks: Decree 356 Điều 29(1)(a) carries the 72 hours on the
 * subject limb while the authority report in the next sub-clause states no period
 * at all. One row would have to pick one period and silently drop the other.
 */
/**
 * How a deadline record's period was sourced.
 *
 * Sprint 65.9.5. `pdpl-deadlines-64.153.0` asserts that every row with
 * `automation_status: 'active'` carries `source_status: 'verified_primary_source'`
 * — and that was the **only** value. So the single way to make the check pass was
 * to CLAIM verification, whether or not anyone had opened the instrument. A gate
 * whose only passing state is an assertion of diligence gets satisfied by
 * stamping, and the stamp is indistinguishable from the work.
 *
 * Two things make the honest state expressible:
 *
 *  - `unverified` and `not_recorded` both PASS. A record may say "this period came
 *    from a secondary summary" or "nobody stated where this came from" and still
 *    be installed. Those are facts about our evidence, not defects in the record.
 *  - a **missing** field FAILS, for a row that claims to be automated. The absence
 *    of a provenance statement is the thing that was invisible; it is now the only
 *    failing state.
 *
 * That inversion is the point. Before it, the check pushed an author toward a
 * false stamp for a green build; after it, the cheapest way to pass is to say what
 * is true.
 *
 * - `verified_primary_source` — the period was read from the instrument's own
 *   signed or officially published text, and that text is retrievable.
 * - `unverified` — authored from a secondary source (a summary, a briefing, a
 *   press release) and deliberately marked as such. Usable; not citable to a
 *   customer as the instrument's own words.
 * - `not_recorded` — no provenance statement was made. The honest value for rows
 *   authored before this field was contracted, and the one the register below
 *   ratchets down.
 */
/**
 * Whether a deadline record's period is the instrument's or ours.
 *
 * Sprint 65.9.5. This field existed with exactly ONE value in the corpus —
 * `active`, 89 times — and no typed vocabulary anywhere. So a record could say
 * "this duty is live" and nothing else, and there was no way to say the thing two
 * `vn-cybersecurity` records actually needed to say: **the duty is real and the
 * period on it is our own control target, not the law's.**
 *
 * Those two records' own `reference` states it — *"internal control target (Law
 * 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)"* — so
 * declaring them `active` would have presented AesirX's 24-hour and 72-hour figures
 * as statutory deadlines. They were left undeclared instead, which cost Vietnamese
 * cybersecurity tenants a visible duty the platform was holding. Both options were
 * wrong, which is how a missing vocabulary value looks from the outside.
 *
 * - `active` — the instrument states this period. It enters the statutory clock
 *   paths, and `deadline-provenance-contract-65.9.5` additionally requires it to
 *   declare how its period was sourced.
 * - `internal_control_target` — the duty is real; the period is AesirX's control
 *   target, pending the instrument that will state one. **Every consumer narrows on
 *   `automation_status: 'active'` in its own query**, so such a record is installed
 *   and countable for coverage and never becomes a clock an operator is told to
 *   meet. That narrowing is load-bearing and is asserted by
 *   `deadline-status-vocabulary-65.9.5`, not assumed.
 * - `stated_maximum` — the instrument states a legal MAXIMUM for the period, not a
 *   period. A ceiling that varies (for example "up to two years, by solution and
 *   field") is recorded with its citation so an operator can read it, and it is
 *   displayed as the instrument's ceiling. It never raises a clock, a reminder or an
 *   overdue state on its own, and no seed, coverage count or statutory path may read
 *   it as a period. Added 2026-10-10 (Sprint 63.24.0, D1 second reading, Q3): the
 *   controlled-testing ceiling was recorded as an active 24-month deadline, so the
 *   platform would have raised a clock the text does not set. The value that was
 *   considered instead, `internal_control_target`, attributes a period to AesirX and
 *   would misstate the source.
 */
export const DEADLINE_AUTOMATION_STATUSES = [
    'active',
    'internal_control_target',
    'stated_maximum',
];
/**
 * The automation values that never raise a clock, a reminder or an overdue state.
 * A query that must not seed or count a clock excludes exactly these (not `active`:
 * most corpus rows carry no `automation_status` at all, so requiring `active` would
 * drop real duties).
 */
export const DEADLINE_NON_CLOCK_AUTOMATION_STATUSES = [
    'internal_control_target',
    'stated_maximum',
];
/**
 * How completely a pack describes its instrument.
 *
 * Sprint 65.9.5 — the corpus carries `complete`, `partial_no_enforcement` and
 * `partial_no_decree`, and **none of them can say "enacted, but the State has not
 * published the text"**.
 *
 * The September stream hit exactly that: Decree 343/2026 was announced and in force
 * while every plausible path to its signed text returned 404, so its pack carried
 * `partial` — which reads as *we have not finished authoring this*, when the fact was
 * *the text does not exist publicly yet*. Those are different facts about different
 * parties, and only one of them is ours to fix. A reader cannot tell which from
 * `partial`.
 */
export const DEADLINE_COMPLETENESS_STATUSES = [
    'complete',
    'partial_no_enforcement',
    'partial_no_decree',
    /**
     * Enacted and in force, but the State has not published the text.
     *
     * Not a gap in our authoring — a gap in what exists publicly. A pack in this
     * state carries what the announcement establishes and nothing more, and must
     * NOT borrow a sibling instrument's periods to look complete.
     */
    'enacted_text_unpublished',
];
export const DEADLINE_SOURCE_STATUSES = [
    'verified_primary_source',
    'unverified',
    'not_recorded',
];
export const REGULATORY_DEADLINE_TYPES = [
    'initial_notification',
    'supplement_response',
    'authority_response',
    'remediation_report',
    'subject_notification',
];
/**
 * fix-sprint #447 W6 (owner ruling OR2) — the units a hand-added regulatory SLA
 * mapping may state its period in.
 *
 * A row added by hand used to carry no unit at all, and a missing unit reads as
 * hours (`RegulatorySlaMapping.deadline_unit`), so "15" typed for fifteen
 * working days was shown as fifteen hours. The create route refuses a row
 * without one of these (422). Seeded rows keep the unit their framework
 * declares, which may be any token the pack uses.
 */
export const REGULATORY_SLA_MAPPING_UNITS = [
    'hours',
    'working_days',
    'calendar_days',
    'days',
    'weeks',
    'months',
    'years',
];
// Sprint 63.8.2 gate E12.5 remediation A — a value, so the route schema's
// `z.enum` is built from this list rather than a second hand-written copy.
export const COORDINATION_TEAM_ROLES = [
    'security',
    'legal',
    'it',
    'management',
    'department_owner',
    'external',
];
//# sourceMappingURL=incident-ops.js.map